import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../../components/ui/use-toast';
import { Loader2, Search, MessageSquare, Check, X, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { useDomainQuery } from '../../../hooks/useDomainQuery';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';

interface Comment {
  id: string;
  post_id: string;
  post_title: string;
  author_name: string;
  author_email: string;
  content: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  parent_id: string | null;
}

export const CommentList = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { applyDomainFilter } = useDomainQuery();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);
  const [deletingComment, setDeletingComment] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [commentToView, setCommentToView] = useState<Comment | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const fetchComments = async () => {
    setLoading(true);
    try {
      // D'abord, récupérer les IDs des articles du domaine courant
      let postsQuery = supabase
        .from('blog_posts')
        .select('id');
      
      // Appliquer le filtre domaine avec fallback
      postsQuery = applyDomainFilter(postsQuery);
      
      const { data: posts, error: postsError } = await postsQuery;
      
      if (postsError) throw postsError;
      
      // Si aucun article n'existe pour ce tenant, retourner un tableau vide
      if (!posts || posts.length === 0) {
        setComments([]);
        setStats({ total: 0, pending: 0, approved: 0, rejected: 0 });
        return;
      }
      
      const postIds = posts.map(post => post.id);
      
      // Récupérer les commentaires pour les articles du tenant
      let query = supabase
        .from('blog_comments')
        .select(`
          *,
          posts:blog_posts(title)
        `)
        .in('post_id', postIds)
        .order('created_at', { ascending: false });

      if (searchQuery) {
        query = query.or(`author_name.ilike.%${searchQuery}%,author_email.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`);
      }

      if (viewMode !== 'all') {
        query = query.eq('status', viewMode);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Transformer les données pour avoir un format plus facile à utiliser
      const formattedComments = data?.map(comment => ({
        ...comment,
        post_title: comment.posts?.title || 'Article inconnu'
      })) || [];

      setComments(formattedComments);

      // Calculer les statistiques
      const pending = formattedComments.filter(comment => comment.status === 'pending').length;
      const approved = formattedComments.filter(comment => comment.status === 'approved').length;
      const rejected = formattedComments.filter(comment => comment.status === 'rejected').length;

      setStats({
        total: formattedComments.length,
        pending,
        approved,
        rejected
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des commentaires:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les commentaires',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [searchQuery, viewMode]);

  const handleDeleteClick = (comment: Comment) => {
    setCommentToDelete(comment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete) return;
    
    setDeletingComment(true);
    try {
      const { error } = await supabase
        .from('blog_comments')
        .delete()
        .eq('id', commentToDelete.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: 'Le commentaire a été supprimé',
      });

      // Mettre à jour la liste des commentaires
      setComments(comments.filter(comment => comment.id !== commentToDelete.id));
      setDeleteDialogOpen(false);
      
      // Mettre à jour les statistiques
      setStats({
        ...stats,
        total: stats.total - 1,
        pending: commentToDelete.status === 'pending' ? stats.pending - 1 : stats.pending,
        approved: commentToDelete.status === 'approved' ? stats.approved - 1 : stats.approved,
        rejected: commentToDelete.status === 'rejected' ? stats.rejected - 1 : stats.rejected
      });
    } catch (error) {
      console.error('Erreur lors de la suppression du commentaire:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de supprimer le commentaire',
      });
    } finally {
      setDeletingComment(false);
    }
  };

  const handleViewComment = (comment: Comment) => {
    setCommentToView(comment);
    setViewDialogOpen(true);
  };

  const handleStatusChange = async (comment: Comment, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from('blog_comments')
        .update({ status: newStatus })
        .eq('id', comment.id);

      if (error) throw error;

      toast({
        title: 'Succès',
        description: `Le commentaire est maintenant ${
          newStatus === 'approved' ? 'approuvé' : 
          newStatus === 'rejected' ? 'rejeté' : 'en attente'
        }`,
      });

      // Mettre à jour la liste des commentaires
      setComments(comments.map(c => 
        c.id === comment.id ? { ...c, status: newStatus } : c
      ));
      
      // Mettre à jour les statistiques
      if (comment.status !== newStatus) {
        setStats({
          ...stats,
          pending: newStatus === 'pending' 
            ? stats.pending + 1 
            : comment.status === 'pending' ? stats.pending - 1 : stats.pending,
          approved: newStatus === 'approved' 
            ? stats.approved + 1 
            : comment.status === 'approved' ? stats.approved - 1 : stats.approved,
          rejected: newStatus === 'rejected' 
            ? stats.rejected + 1 
            : comment.status === 'rejected' ? stats.rejected - 1 : stats.rejected
        });
      }
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Impossible de modifier le statut du commentaire',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1">Commentaires</h1>
            <p className="text-gray-400">Gérez les commentaires des articles de blog</p>
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 text-white rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
                placeholder="Rechercher un commentaire..."
              />
            </div>

            <div>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as 'all' | 'pending' | 'approved' | 'rejected')}
                className="block w-full py-2.5 px-3 bg-white/5 border border-white/10 text-white rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvés</option>
                <option value="rejected">Rejetés</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des commentaires */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Aucun commentaire</h3>
              <p className="text-gray-400">
                {searchQuery
                  ? 'Aucun commentaire ne correspond à votre recherche'
                  : viewMode !== 'all'
                  ? `Aucun commentaire avec le statut "${viewMode}"`
                  : 'Aucun commentaire pour le moment'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Auteur
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Commentaire
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Article
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {comments.map((comment, index) => (
                    <tr key={comment.id} className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-purple-400/10 rounded-full flex items-center justify-center">
                            <div className="text-purple-400 font-medium">
                              {comment.author_name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{comment.author_name}</div>
                            <div className="text-sm text-gray-400">{comment.author_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white line-clamp-2">{comment.content}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white line-clamp-1">{comment.post_title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {format(new Date(comment.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge
                          className={
                            comment.status === 'approved'
                              ? 'bg-green-400/20 text-green-400 border-green-400/20'
                              : comment.status === 'rejected'
                              ? 'bg-red-400/20 text-red-400 border-red-400/20'
                              : 'bg-yellow-400/20 text-yellow-400 border-yellow-400/20'
                          }
                        >
                          {comment.status === 'approved'
                            ? 'Approuvé'
                            : comment.status === 'rejected'
                            ? 'Rejeté'
                            : 'En attente'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewComment(comment)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {comment.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(comment, 'approved')}
                                className="text-green-400 hover:text-green-300"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusChange(comment, 'rejected')}
                                className="text-red-400 hover:text-red-300"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(comment)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialogue de visualisation du commentaire */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Détails du commentaire</DialogTitle>
          </DialogHeader>

          {commentToView && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 bg-purple-400/10 rounded-full flex items-center justify-center mr-3">
                    <div className="text-purple-400 font-medium">
                      {commentToView.author_name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-white">{commentToView.author_name}</div>
                    <div className="text-sm text-gray-400">{commentToView.author_email}</div>
                  </div>
                  <div className="ml-auto text-sm text-gray-400">
                    {format(new Date(commentToView.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                  </div>
                </div>
                <div className="text-white whitespace-pre-wrap">{commentToView.content}</div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-sm text-gray-400 mb-1">Article</div>
                <div className="text-white">{commentToView.post_title}</div>
              </div>

              <div className="flex justify-between items-center">
                <Badge
                  className={
                    commentToView.status === 'approved'
                      ? 'bg-green-400/20 text-green-400 border-green-400/20'
                      : commentToView.status === 'rejected'
                      ? 'bg-red-400/20 text-red-400 border-red-400/20'
                      : 'bg-yellow-400/20 text-yellow-400 border-yellow-400/20'
                  }
                >
                  {commentToView.status === 'approved'
                    ? 'Approuvé'
                    : commentToView.status === 'rejected'
                    ? 'Rejeté'
                    : 'En attente'}
                </Badge>

                {commentToView.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        handleStatusChange(commentToView, 'approved');
                        setViewDialogOpen(false);
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Approuver
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        handleStatusChange(commentToView, 'rejected');
                        setViewDialogOpen(false);
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      <X className="mr-2 h-4 w-4" />
                      Rejeter
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setViewDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-black/95 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Supprimer le commentaire</DialogTitle>
            <DialogDescription className="text-gray-400">
              Êtes-vous sûr de vouloir supprimer ce commentaire ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          {commentToDelete && (
            <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl">
              <div className="flex items-center mb-2">
                <div className="h-6 w-6 bg-purple-400/10 rounded-full flex items-center justify-center mr-2">
                  <div className="text-purple-400 text-xs font-medium">
                    {commentToDelete.author_name.charAt(0).toUpperCase()}
                  </div>
                </div>
                <span className="font-medium text-white">{commentToDelete.author_name}</span>
              </div>
              <p className="mt-2 text-sm text-gray-400 line-clamp-3">{commentToDelete.content}</p>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deletingComment}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {deletingComment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CommentList;
